import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useExcIncreaseV1WireQueryServiceWireGet } from '@interxfi/api-client/generated/hooks/exc-increase-v1-wire-query-service-wire-get';
import { useActiveRealmAPIContext } from '@interxfi/api-client/realm-context';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ExcIncreaseV1WireState } from '@interxfi/types/generated/api';
import { IncreaseWireSummaryBar } from '../../../../../../../../../../features/vendor/increase/wire/increase-wire-ach-summary-bar';
import { IncreaseWireEntityMenu } from '../../../../../../../../../../features/vendor/increase/wire/increase-wire-entity-menu';
import { IncreaseWireEventSummary } from '../../../../../../../../../../features/vendor/increase/wire/increase-wire-event';
import { getIncreaseWireStateListItems } from '../../../../../../../../../../features/vendor/increase/wire/increase-wire-list-items';
import { getIncreaseWireDetailPath } from '../../../../../../../../../../features/vendor/increase/wire/increase-wire-routing';
import { IncreaseWireStatusPill } from '../../../../../../../../../../features/vendor/increase/wire/increase-wire-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/increase/wire/_wire/$wireId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `increase-wire-${params.wireId}`,
        label: 'Wire',
        to: getIncreaseWireDetailPath(params.wireId),
        suffix: <CopyButton content={params.wireId} label="Copy Wire ID" tooltip={{ copy: params.wireId, copied: 'Copied Wire ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Increase Wire'),
  component: IncreaseWireDetail,
});

function getListItems(data: ExcIncreaseV1WireState | undefined, isSimulated: boolean) {
  const items = getIncreaseWireStateListItems(data, { increaseId: !isSimulated });

  return {
    left: [
      {
        id: 'main',
        items: [
          items.data.increaseId,
          items.data.spec.amount,
          items.data.spec.isInbound,
          { ...items.data.spec.beneficiary.name, title: 'Beneficiary Name' },
          { ...items.data.spec.beneficiary.address.formatted, title: 'Beneficiary Address' },
          ...(items.data.spec.originator
            ? [
                { ...items.data.spec.originator.name, title: 'Originator Name' },
                { ...items.data.spec.originator.address.formatted, title: 'Originator Address' },
              ]
            : []),
          ...(items.data.spec.messageToRecipient ? [items.data.spec.messageToRecipient] : []),
        ],
      },
      {
        id: 'bankDetails',
        title: 'Bank Details',
        items: [
          items.data.spec.bankDetails.routingNumber,
          items.data.spec.bankDetails.accountNumber,
          { ...items.data.spec.bankDetails.accountHolderName, title: 'Name on Account' },
          items.data.spec.bankDetails.accountType,
        ],
      },
    ] as const satisfies ReadonlyListGroup[],
    right: [
      items.metadata.createdAt,
      items.metadata.updatedAt,
      items.wireId,
      { ...items.data.spec.wireId, title: 'Payment Wire ID' },
      items.data.spec.sourceId,
      items.data.spec.transferId,
      items.data.spec.programId,
      items.data.spec.accountId,
      items.data.spec.fedwireSubmission,
      items.data.spec.inputMessageAccountabilityData,
    ] as const satisfies ReadonlyListItem[],
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load Increase wire', 'view this Increase wire');

function IncreaseWireDetail() {
  const { wireId } = Route.useParams();
  const request = { wireId } as const;
  const { activeRealmIsSimulated } = useActiveRealmAPIContext();
  const { data, error, isPending } = useExcIncreaseV1WireQueryServiceWireGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.wire, activeRealmIsSimulated), [data?.wire, activeRealmIsSimulated]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <IncreaseWireStatusPill isPending={showPending} size="large" status={data?.wire?.status} />
          </HorizontalStack>

          <IncreaseWireEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <IncreaseWireSummaryBar {...request} />
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <IncreaseWireEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
